import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { firstValueFrom, of } from 'rxjs';
import { catchError, filter, take, timeout } from 'rxjs/operators';
import { selectAuthStatus } from '../store/auth/auth.selectors';
import { decodeJwtClaims, isJwtExpired } from '../core/auth/jwt';

function defaultPostLoginUrl(accessToken: string): string {
    const claims = decodeJwtClaims(accessToken);
    const role = String(claims?.role || '').trim().toLowerCase();
    if (role === 'app_admin') return '/admin/dashboard';
    if (role === 'investor') return '/properties';
    if (role === 'subpartner' || role === 'sales') return '/developer/dashboard';
    return '/developer/dashboard';
}

/**
 * Prevents already-authenticated users from seeing auth pages (login, signup).
 * If the user is logged in, redirects to returnUrl (if safe) or role-based dashboard.
 */
export const AlreadyAuthenticatedGuard: CanActivateFn = async (_route, state): Promise<boolean | UrlTree> => {
    const store = inject(Store);
    const router = inject(Router);

    const snapshot = await firstValueFrom(
        store.select(selectAuthStatus).pipe(
            take(1),
            catchError(() => of(null as any))
        )
    );

    // If we can immediately confirm the user is authenticated, redirect.
    if (snapshot?.isAuthenticated === true && snapshot?.accessToken) {
        const claims = decodeJwtClaims(snapshot.accessToken);
        if (claims && !isJwtExpired(claims)) {
            const returnUrl = _route.queryParamMap?.get('returnUrl');
            const dest = returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('/login') && !returnUrl.startsWith('/signup')
                ? returnUrl
                : defaultPostLoginUrl(snapshot.accessToken);
            return router.parseUrl(dest);
        }
    }

    // If auth state is unknown (null), wait briefly for initialization.
    if (snapshot?.isAuthenticated === null) {
        try {
            const resolved = await firstValueFrom(
                store.select(selectAuthStatus).pipe(
                    filter((s) => s.isAuthenticated !== null && !s.loading),
                    take(1),
                    timeout(3000),
                    catchError(() => of(null as any))
                )
            );
            if (resolved?.isAuthenticated === true && resolved?.accessToken) {
                const claims = decodeJwtClaims(resolved.accessToken);
                if (claims && !isJwtExpired(claims)) {
                    const returnUrl = _route.queryParamMap?.get('returnUrl');
                    const dest = returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('/login') && !returnUrl.startsWith('/signup')
                        ? returnUrl
                        : defaultPostLoginUrl(resolved.accessToken);
                    return router.parseUrl(dest);
                }
            }
        } catch {
            // Timeout or error — let the page render (user is not confirmed authenticated).
        }
    }

    return true;
};
